import { apiFunction } from '../../Helpers/apiHelper'

export interface BlackoutDate {
  id: number
  date: string
  reason: string | null
  is_active: boolean
}

interface ApiResponse<T> {
  data: T
}

interface BlackoutDateResponse {
  message: string
  data: BlackoutDate
}

interface BlackoutDateListResponse {
  data: BlackoutDate[]
}

/**
 * 予約不可日一覧を取得
 * 
 * @param filters フィルター条件
 * @returns 予約不可日一覧
 * @throws Error API呼び出し失敗時
 */
export const getBlackoutDates = async (filters?: {
  date_from?: string
  date_to?: string
  is_active?: boolean
}): Promise<BlackoutDate[]> => {
  try {
    const queryParams = new URLSearchParams()
    if (filters?.date_from) {
      queryParams.append('date_from', filters.date_from)
    }
    if (filters?.date_to) {
      queryParams.append('date_to', filters.date_to)
    }
    if (filters?.is_active !== undefined) {
      queryParams.append('is_active', filters.is_active.toString())
    }

    const url = `/api/blackout-dates${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const result = await apiFunction<BlackoutDateListResponse>(
      url,
      {},
      'GET'
    )
    
    if (!result || !result.data) {
      throw new Error('予約不可日データの形式が正しくありません。')
    }

    // APIレスポンスのisActiveをis_activeに変換
    return result.data.map(item => ({
      ...item,
      is_active: (item as any).isActive !== undefined ? (item as any).isActive : item.is_active
    }))
  } catch (error: any) {
    if (error.message) {
      throw new Error(error.message)
    }
    throw new Error('予約不可日の取得に失敗しました。')
  }
}

/**
 * 予約不可日を作成
 * 
 * @param data 作成データ
 * @returns 作成された予約不可日
 * @throws Error API呼び出し失敗時
 */
export const createBlackoutDate = async (data: {
  date: string
  reason: string
  is_active?: boolean
}): Promise<BlackoutDate> => {
  try {
    const result = await apiFunction<BlackoutDateResponse>(
      '/api/blackout-dates',
      {
        date: data.date,
        reason: data.reason.trim(),
        is_active: data.is_active !== undefined ? data.is_active : true
      },
      'POST'
    )
    
    if (!result || !result.data) {
      throw new Error('予約不可日の作成に失敗しました。')
    }

    // APIレスポンスのisActiveをis_activeに変換
    return {
      ...result.data,
      is_active: (result.data as any).isActive !== undefined ? (result.data as any).isActive : result.data.is_active
    }
  } catch (error: any) {
    if (error.statusCode === 400 || error.statusCode === 422) {
      throw new Error(error.message || '予約不可日を作成できませんでした。')
    }
    if (error.message) {
      throw new Error(error.message)
    }
    throw new Error('予約不可日の作成に失敗しました。')
  }
}

/**
 * 予約不可日を更新
 * 
 * @param id 予約不可日ID
 * @param data 更新データ
 * @returns 更新された予約不可日
 * @throws Error API呼び出し失敗時
 */
export const updateBlackoutDate = async (
  id: number,
  data: {
    date?: string
    reason?: string
    is_active?: boolean
  }
): Promise<BlackoutDate> => {
  try {
    const updateData: any = {}
    if (data.date !== undefined) updateData.date = data.date
    if (data.reason !== undefined) updateData.reason = data.reason
    if (data.is_active !== undefined) updateData.is_active = data.is_active

    const result = await apiFunction<BlackoutDateResponse>(
      `/api/blackout-dates/${id}`,
      updateData,
      'PUT'
    )
    
    if (!result || !result.data) {
      throw new Error('予約不可日の更新に失敗しました。')
    }

    // APIレスポンスのisActiveをis_activeに変換
    return {
      ...result.data,
      is_active: (result.data as any).isActive !== undefined ? (result.data as any).isActive : result.data.is_active
    }
  } catch (error: any) {
    if (error.statusCode === 400 || error.statusCode === 404 || error.statusCode === 422) {
      throw new Error(error.message || '予約不可日を更新できませんでした。')
    }
    if (error.message) {
      throw new Error(error.message)
    }
    throw new Error('予約不可日の更新に失敗しました。')
  }
}

/**
 * 予約不可日を削除
 * 
 * @param id 予約不可日ID
 * @returns void
 * @throws Error API呼び出し失敗時
 */
export const deleteBlackoutDate = async (id: number): Promise<void> => {
  try {
    await apiFunction<{ message: string }>(
      `/api/blackout-dates/${id}`,
      {},
      'DELETE'
    )
  } catch (error: any) {
    if (error.statusCode === 404) {
      throw new Error('予約不可日が見つかりません。')
    }
    if (error.message) {
      throw new Error(error.message)
    }
    throw new Error('予約不可日の削除に失敗しました。')
  }
}

